/**
 * mcpKeyHelpers.js — pure helpers for MCP API key create form
 *
 * Covers:
 *   R49.1  — expiresAt normalization to end-of-day UTC before POST
 *   R49.5  — key name validation (1–100 chars, trimmed, no whitespace-only)
 *
 * These were extracted from AccountSecurityPage.jsx createMcpKey() so that
 * both the production component and the test suite import the same code —
 * preventing divergent-copy false-passes (zoe W2-1).
 *
 * All functions are pure (no React, no fetch, no DOM).
 */

/**
 * Given a "YYYY-MM-DD" date-only string, returns an ISO-8601 UTC timestamp
 * set to 23:59:59.999Z of that day — i.e. the key is valid through the end
 * of the UTC day the user selected (R49.1 convention).
 *
 * Given an empty / falsy value (the user left the expiry blank), returns null
 * so the caller can omit the field from the POST body entirely (R49.5:
 * no null fallback — the field is simply absent).
 *
 * @param {string} dateOnly  "YYYY-MM-DD" string from a <input type="date">
 * @returns {string|null}    ISO-8601 timestamp string, or null when no date given
 */
export function normalizeExpiryToEndOfDayUTC(dateOnly) {
  if (!dateOnly) return null;
  return new Date(dateOnly + 'T23:59:59.999Z').toISOString();
}

/**
 * Validates the MCP key name field (mirrors the backend 400 checks so the
 * UI surface and the backend stay in sync).
 *
 * Rules (applied after trimming):
 *   - Required: blank / whitespace-only → invalid
 *   - Max length: > 100 chars → invalid; exactly 100 → valid
 *
 * @param {string} name  Raw value from the key-name input (may have leading/trailing whitespace)
 * @returns {{ valid: true, trimmed: string } | { valid: false, error: string }}
 */
export function validateMcpKeyName(name) {
  const trimmed = name.trim();
  if (trimmed.length === 0) {
    return { valid: false, error: 'Key name is required.' };
  }
  if (trimmed.length > 100) {
    return { valid: false, error: 'Key name must be 100 characters or fewer.' };
  }
  return { valid: true, trimmed };
}
